/**
 * Chat Assistant — WebSocket connection manager.
 *
 * Integration guide §1 (Connection & Authentication), §10 (Error Handling),
 * §12 (Reconnect Strategy).
 *
 * Responsibilities kept here (transport only):
 *   • open the socket with a fresh Cognito **access** token in the query string
 *   • classify a connect-time rejection as an auth failure and refresh the token
 *   • bounded exponential backoff with jitter for unexpected disconnects
 *   • hand parsed frames to the store; never interpret them
 *   • never emit an un-redacted URL to any log sink
 *
 * Frame semantics live in `store.ts`.
 */

import {
    CHAT_WS_AUTH_MODE,
    RECONNECT,
    backoffDelay,
    buildSocketUrl,
    isChatAssistantConfigured,
    redactToken,
} from "./config";
import { describeAccessToken, getAccessToken, refreshAccessToken } from "./auth";
import type { AccessTokenReport } from "./auth";
import type {
    ClientAction,
    GatewayErrorFrame,
    ServerEvent,
    SocketError,
    SocketStatus,
} from "./types";

export interface SocketHandlers {
    /** Status transitions, mirrored into `socketStatus` (guide §11). */
    onStatus: (status: SocketStatus) => void;
    /** A parsed server frame (guide §5). */
    onEvent: (event: ServerEvent) => void;
    /** A transport-level failure that has no `error` frame behind it. */
    onError: (error: SocketError) => void;
    /**
     * Fired every time the socket reaches `open`, including after a reconnect.
     * Guide §12: the store replays `listChannels` + `loadHistory` here — and
     * deliberately never replays `sendMessage`.
     */
    onOpen: () => void;
}

/**
 * Why a connect attempt closed before it ever opened.
 *
 * Guide §10 row 1 attributes this to a bad or expired token — but a TLS failure,
 * a DNS failure (an endpoint that no longer resolves), an offline machine or a
 * blocked port all close before opening too. Browsers expose neither the
 * handshake status nor the network error to script, so the close event alone
 * cannot tell them apart, and guessing "auth" sends the user to a sign-in screen
 * that cannot fix a connectivity problem.
 *
 * The token just presented is the available discriminator: a valid, unexpired
 * access token means the server never got the chance to reject it.
 */
export type ConnectFailureCause = "credentials" | "unreachable";

export interface ConnectFailure {
    cause: ConnectFailureCause;
    message: string;
}

export function classifyConnectFailure(
    tokenReport: AccessTokenReport | null,
    options: { localHttps: boolean } = { localHttps: false },
): ConnectFailure {
    if (!tokenReport || !tokenReport.valid) {
        return {
            cause: "credentials",
            message: "The assistant rejected this session. Sign in again to reconnect.",
        };
    }

    const hint = options.localHttps
        ? " If the dev server is running over HTTPS, check that the local certificate is trusted."
        : "";

    return {
        cause: "unreachable",
        message: `Can't reach the assistant — the connection closed before the server responded. Check your network connection.${hint}`,
    };
}

/** True when the page itself is served over HTTPS from a local dev origin. */
function isLocalHttpsOrigin(): boolean {
    if (typeof window === "undefined") return false;
    return (
        window.location.protocol === "https:" &&
        /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
    );
}

/** Normal-closure code; anything else is treated as unexpected. */
const CLOSE_NORMAL = 1000;

export class ChatAssistantSocket {
    private handlers: SocketHandlers;
    private ws: WebSocket | null = null;
    private status: SocketStatus = "closed";

    /** Set by `disconnect()` so a deliberate close does not trigger a reconnect. */
    private manualClose = false;
    /** True once the *current* attempt fired `onopen`. */
    private openedThisAttempt = false;
    /** Guards against overlapping connect attempts. */
    private connecting = false;

    /** Backoff counter for unexpected disconnects (guide §12). */
    private reconnectAttempt = 0;
    /** Consecutive connect-time rejections (guide §10, first row). */
    private authRetries = 0;
    /** Consecutive attempts that closed before opening, for any reason. */
    private connectFailures = 0;
    /** Verdict on the token handed to the current attempt; drives the diagnosis. */
    private lastTokenReport: AccessTokenReport | null = null;
    /** Ask for a forcibly refreshed token on the next attempt. */
    private forceRefresh = false;

    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(handlers: SocketHandlers) {
        this.handlers = handlers;
    }

    getStatus(): SocketStatus {
        return this.status;
    }

    isOpen(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    /**
     * Open the socket. Safe to call repeatedly — an in-flight or established
     * connection short-circuits.
     */
    async connect(): Promise<void> {
        if (this.connecting || this.isOpen()) return;

        if (!isChatAssistantConfigured()) {
            this.setStatus("closed");
            this.handlers.onError({
                kind: "config",
                message:
                    "Chat assistant is not configured. Set REACT_APP_CHAT_WS_URL to this " +
                    "environment's API Gateway WebSocket endpoint.",
            });
            return;
        }

        this.connecting = true;
        this.manualClose = false;
        this.openedThisAttempt = false;
        this.clearReconnectTimer();
        this.setStatus("connecting");

        // Guide §12: ensure the access token is valid before reconnecting;
        // refresh it when the previous attempt was rejected at connect time.
        const token = this.forceRefresh ? await refreshAccessToken() : await getAccessToken();
        this.forceRefresh = false;

        if (!token) {
            this.connecting = false;
            this.setStatus("auth_failed");
            this.handlers.onError({
                kind: "auth",
                message: "No Cognito session. Sign in again to use the assistant.",
            });
            return;
        }

        // Diagnosis always keys off the Cognito session, in either mode: it is
        // what distinguishes "credentials are bad" from "cannot reach the server"
        // when a connect attempt closes before opening (see classifyConnectFailure).
        this.lastTokenReport = describeAccessToken(token);

        // Guide §3 — legacy flow only, and only when explicitly switched on. The
        // ticket is single-use, so it is fetched fresh for every attempt rather
        // than cached across reconnects.
        let credential = token;
        if (CHAT_WS_AUTH_MODE === "ticket") {
            try {
                // Loaded on demand so the default token flow never pulls the REST
                // client (and axios) into its module graph — the legacy path stays
                // genuinely inert, not merely unused.
                const { requestChatAssistantTicket } = await import("./api");
                credential = await requestChatAssistantTicket();
            } catch (error) {
                this.connecting = false;
                this.setStatus("closed");
                this.handlers.onError({
                    kind: "network",
                    message:
                        (error as Error)?.message ||
                        "Could not obtain a connection ticket from the assistant.",
                });
                this.scheduleReconnect();
                return;
            }
        }

        let url: string;
        try {
            url = buildSocketUrl(credential);
        } catch (error) {
            this.connecting = false;
            this.setStatus("closed");
            this.handlers.onError({
                kind: "config",
                message: (error as Error).message,
            });
            return;
        }

        try {
            const ws = new WebSocket(url);
            this.ws = ws;

            ws.onopen = () => {
                this.connecting = false;
                this.openedThisAttempt = true;
                this.reconnectAttempt = 0;
                this.authRetries = 0;
                this.connectFailures = 0;
                this.setStatus("open");
                this.handlers.onOpen();
            };

            ws.onmessage = (event) => this.handleFrame(event.data);

            ws.onerror = () => {
                // The browser exposes no detail for a socket error, and never the
                // handshake status. `onclose` always follows, and carries the
                // information we act on.
                this.log("socket error", url);
            };

            ws.onclose = (event) => this.handleClose(event.code, url);
        } catch (error) {
            this.connecting = false;
            this.ws = null;
            this.handlers.onError({
                kind: "network",
                message: (error as Error).message || "Could not open the assistant connection.",
            });
            this.scheduleReconnect();
        }
    }

    /**
     * Send one client action (guide §4). Returns false when the socket is not
     * open, so callers can surface that instead of silently dropping intent.
     */
    send(action: ClientAction): boolean {
        if (!this.isOpen() || !this.ws) return false;
        try {
            this.ws.send(JSON.stringify(action));
            return true;
        } catch (error) {
            this.handlers.onError({
                kind: "network",
                message: (error as Error).message || "Failed to send to the assistant.",
            });
            return false;
        }
    }

    /** Close deliberately. No reconnect follows. */
    disconnect(): void {
        this.manualClose = true;
        this.clearReconnectTimer();
        this.connecting = false;
        const ws = this.ws;
        this.ws = null;
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
            ws.close(CLOSE_NORMAL, "client disconnect");
        }
        this.setStatus("closed");
    }

    /** Drop the backoff/auth counters — used when the user retries by hand. */
    resetRetryState(): void {
        this.reconnectAttempt = 0;
        this.authRetries = 0;
        this.connectFailures = 0;
        this.forceRefresh = true;
    }

    // ─── internals ─────────────────────────────────────────────────────────

    private handleFrame(raw: unknown): void {
        if (typeof raw !== "string") return;
        let frame: ServerEvent;
        try {
            frame = JSON.parse(raw);
        } catch {
            this.handlers.onError({
                kind: "server",
                message: "Received a malformed frame from the assistant.",
            });
            return;
        }
        if (!frame || typeof (frame as ServerEvent).type !== "string") {
            const gateway = frame as unknown as GatewayErrorFrame;
            if (gateway && typeof gateway.message === "string") {
                this.handlers.onError({
                    kind: "server",
                    message: gateway.requestId
                        ? `${gateway.message} (request ${gateway.requestId})`
                        : gateway.message,
                });
            }
            return;
        }
        this.handlers.onEvent(frame);
    }

    private handleClose(code: number, url: string): void {
        this.connecting = false;
        this.ws = null;

        if (this.manualClose) {
            this.setStatus("closed");
            return;
        }

        // Closed without ever opening. Guide §10 row 1 attributes this to a bad
        // or expired token, but a TLS/DNS/network failure is indistinguishable
        // from the close event alone — so classify it from the token presented.
        if (!this.openedThisAttempt) {
            this.connectFailures += 1;
            const failure = classifyConnectFailure(this.lastTokenReport, {
                localHttps: isLocalHttpsOrigin(),
            });
            this.log(`connect failed (${failure.cause}, attempt ${this.connectFailures})`, url);

            if (failure.cause === "credentials") {
                this.authRetries += 1;

                if (this.authRetries > RECONNECT.maxAuthRetries) {
                    // Only a genuinely bad credential is terminal — signing in
                    // again is an action the user can actually take.
                    this.setStatus("auth_failed");
                    this.handlers.onError({ kind: "auth", message: failure.message });
                    return;
                }

                // Refresh the access token, then retry (guide §10 client action).
                this.forceRefresh = true;
                this.scheduleReconnect(this.authRetries - 1);
                return;
            }

            // The credential is valid, so this is connectivity. Keep reconnecting
            // rather than dead-ending the user on a sign-in prompt they cannot
            // act on. Reported on the first failure, then occasionally.
            if (this.connectFailures === 1 || this.connectFailures % 5 === 0) {
                this.handlers.onError({ kind: "network", message: failure.message });
            }
            this.setStatus("closed");
            this.scheduleReconnect();
            return;
        }

        this.log(`socket closed (code ${code})`, url);
        this.setStatus("closed");
        this.scheduleReconnect();
    }

    /** Guide §12: bounded exponential backoff with jitter. */
    private scheduleReconnect(attemptOverride?: number): void {
        if (this.manualClose) return;
        this.clearReconnectTimer();

        const attempt = attemptOverride ?? this.reconnectAttempt;
        const delay = backoffDelay(attempt);
        this.reconnectAttempt = attempt + 1;
        this.setStatus("connecting");

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            void this.connect();
        }, delay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    private setStatus(status: SocketStatus): void {
        if (this.status === status) return;
        this.status = status;
        this.handlers.onStatus(status);
    }

    /**
     * Guide §12 Security Note — the access token rides in the query string, so
     * every URL is redacted before it can reach the console or a telemetry sink.
     */
    private log(message: string, url: string): void {
        if (process.env.NODE_ENV === "production") return;
        // eslint-disable-next-line no-console
        console.warn(`[chat-assistant] ${message} — ${redactToken(url)}`);
    }
}
